import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {salaryGetAll, salaryExcel} from '../../../redux/slices/staff.slice'
import {projectLookup, staffGetAll} from '../../../redux/slices/lookup.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectStaffData = (state) => ({
  salary: state.staff.salary,
  loading: state.staff.loading,
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  staffLookup: state.lookup.staffLookup,
})

function List() {
  const intl = useIntl()
  const {salary, loading} = useSelector(selectStaffData)
  const {projectLookupList, staffLookup} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(salaryGetAll(params))
  }, [params, dispatch])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(staffGetAll())
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      salaryExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.SALARY'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.SEARCH.NAMESURNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'staffNameSurname',
      Cell: ({...props}) => <CustomCellDouble item={props} double='name' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.SEARCH.PROJECT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.SEARCH.PROGRESS_PAYMENT_DATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'progressPaymentDate',
      Cell: ({...props}) => <CustomCell item={props} date='month' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.SEARCH.PAYMENT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'payment',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.SEARCH.BALANCE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'balance',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'STAFF.SEARCH.PROJECT',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'STAFF.SEARCH.NAMESURNAME',
          data: staffLookup,
          name: 'staffId',
        },
        {
          title: 'STAFF.SEARCH.PROGRESS_PAYMENT_DATE',
          type: 'date',
          name: 'progressPaymentDate',
        },
      ],
    },
  ], [handleExcelExport, projectLookupList, staffLookup])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.SALARY'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'SALARY'}
        />
        <KTCardBody>
          {!salary && <ListLoading />}
          {salary && (
            <Table
              items={salary}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
